import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Info, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/ui-bits";
import { PeriodSelector } from "@/components/dashboard/PeriodSelector";
import { SummaryCards } from "@/components/dashboard/SummaryCards";
import { MonthlyChart } from "@/components/dashboard/MonthlyChart";
import { CategoryBreakdown, VendorTotals } from "@/components/dashboard/Breakdowns";
import { BudgetAlerts, OverallBudgetCard } from "@/components/dashboard/BudgetWidgets";
import { OutstandingPanel } from "@/components/bills/OutstandingPanel";
import { api, errMsg } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function DashboardPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const reqId = useRef(0);

  const load = useCallback(async (p) => {
    const id = ++reqId.current;
    try {
      const { data: d } = await api.get("/dashboard", { params: { start: p.start, end: p.end } });
      if (id === reqId.current) { setData(d); setError(""); }
    } catch (e) {
      if (id === reqId.current) setError(errMsg(e));
    }
  }, []);

  useEffect(() => { if (period) load(period); }, [period, load]);

  return (
    <div>
      <PageHeader eyebrow={`Welcome back, ${user.name.split(" ")[0]}`} title="Dashboard" testId="dashboard-title">
        <Button asChild className="rounded-xl h-10" data-testid="dashboard-add-entry-button"><Link to="/add"><Plus className="h-4 w-4 mr-1" />Add Entry</Link></Button>
      </PageHeader>
      <OutstandingPanel />
      <div className="surface p-3 sm:p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 fade-up">
        <PeriodSelector onChange={setPeriod} />
        {data && <div className="text-xs text-muted-foreground" data-testid="dashboard-period-label">{formatDate(data.period.start)} – {formatDate(data.period.end)}</div>}
      </div>
      {error && <div className="mb-4 text-sm text-rose-700" data-testid="dashboard-error">{error}</div>}
      {!data ? <div className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading…</div> : (
        <>
          <BudgetAlerts alerts={data.budget_alerts} month={data.budget_month} />
          <SummaryCards totals={data.totals} />
          {data.totals.count === 0 && (
            <div className="mt-4 flex items-center gap-2 rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground" data-testid="dashboard-no-data">
              <Info className="h-4 w-4" /> No data for this period
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
            <div className="lg:col-span-8 min-w-0"><MonthlyChart data={data.monthly} filename={`DaLilShop_Dashboard_IncomeVsExpenses_${data.monthly[0].month}_to_${data.monthly[data.monthly.length - 1].month}.png`} /></div>
            <div className="lg:col-span-4 min-w-0"><CategoryBreakdown rows={data.expense_by_category} filename={`DaLilShop_Dashboard_ExpensesByCategory_${data.period.start}_to_${data.period.end}.png`} /></div>
            <div className="lg:col-span-7 min-w-0"><VendorTotals rows={data.vendor_totals} /></div>
            <div className="lg:col-span-5 min-w-0"><OverallBudgetCard overall={data.overall_budget} month={data.budget_month} /></div>
          </div>
        </>
      )}
    </div>
  );
}
